import {
    ActivityCategoryInfo,
    BracketAdjustmentMode,
    MatchRoundConfig,
} from "../../interface";

export const MAX_PARALLEL_COURTS = 5;

type CategoryAdjustmentFields = Pick<
    ActivityCategoryInfo,
    'bracket_adjustment_mode' | 'bracket_recommended_adjustment_mode' | 'bracket_allowed_adjustment_modes'
>;

export function configuredCategoryAdjustmentMode(
    category: CategoryAdjustmentFields,
): BracketAdjustmentMode {
    return (
        category.bracket_adjustment_mode ||
        category.bracket_recommended_adjustment_mode ||
        category.bracket_allowed_adjustment_modes?.[0] ||
        "perfect"
    );
}

export function categoryRoundNamesForMode(
    category: ActivityCategoryInfo,
    mode: BracketAdjustmentMode,
): string[] {
    return category.bracket_round_names_by_mode?.[mode] || category.round_names || [];
}

export function categoryHasBronzeMatch(structure: string | undefined): boolean {
    return structure !== "SingleEliminationNoThirdPlace";
}

// Both single-elimination variants share one draw. The tournament match type
// is only the default, so a category can switch bronze on or off either way.
export function bracketStructureOptionsFor(matchType: string): { value: string; label: string }[] {
    if (matchType === "SingleElimination" || matchType === "SingleEliminationNoThirdPlace") {
        return [
            { value: "SingleElimination", label: "Single Elimination (with Bronze Match)" },
            {
                value: "SingleEliminationNoThirdPlace",
                label: "Single Elimination (No Bronze Match)",
            },
        ];
    }
    return [{ value: matchType, label: matchType }];
}

export function categoryHasExistingMatches(
    category?: Pick<ActivityCategoryInfo, 'is_bracket_done' | 'bracket_status'> | null,
): boolean {
    return Boolean(category?.is_bracket_done || category?.bracket_status === 'published');
}

export function categorySetupAdjustmentMode(
    category: CategoryAdjustmentFields & Pick<ActivityCategoryInfo, 'is_bracket_done' | 'bracket_status'>,
): BracketAdjustmentMode {
    if (categoryHasExistingMatches(category)) {
        return configuredCategoryAdjustmentMode(category);
    }
    return category.bracket_recommended_adjustment_mode || configuredCategoryAdjustmentMode(category);
}

function bitLen(value: number): number {
    if (value <= 0) {
        return 0;
    }
    return Math.floor(Math.log2(value)) + 1;
}

function isPowerOfTwo(value: number): boolean {
    return value > 0 && (value & (value - 1)) === 0;
}

function closestPowerOfTwo(value: number): number {
    if (value <= 1) {
        return 1;
    }
    return 1 << (bitLen(value) - 1);
}

export function maxParallelCourts(playableMatches: number, tournamentCourts = MAX_PARALLEL_COURTS): number {
    const playableCap = playableMatches < 1 ? 1 : Math.min(MAX_PARALLEL_COURTS, playableMatches);
    const venueCap = tournamentCourts > 0 ? tournamentCourts : MAX_PARALLEL_COURTS;
    return Math.max(1, Math.min(playableCap, venueCap));
}

export function playableMatchCountsPerRound(
    participantCount: number,
    adjustmentMode: BracketAdjustmentMode,
    hasBronzeMatch: boolean,
): number[] {
    if (participantCount < 2) {
        return [];
    }

    let mainDrawSize = participantCount;
    let preliminaryMatchCount = 0;
    let byeCount = 0;
    let mainRoundOffset = 0;
    let mode: BracketAdjustmentMode = "perfect";

    if (!isPowerOfTwo(participantCount)) {
        const lowerPower = closestPowerOfTwo(participantCount);
        if (adjustmentMode === "bye") {
            mode = "bye";
            mainDrawSize = lowerPower * 2;
            byeCount = mainDrawSize - participantCount;
        } else {
            mode = "extra";
            mainDrawSize = lowerPower;
            preliminaryMatchCount = participantCount - lowerPower;
            mainRoundOffset = 1;
        }
    }

    const winnerBracketRoundCount = bitLen(mainDrawSize) - 1 + mainRoundOffset;
    const counts = Array.from({ length: winnerBracketRoundCount }, (_, roundIndex) => {
        if (mode === "extra" && roundIndex === 0) {
            return preliminaryMatchCount;
        }
        const mainRoundIndex = roundIndex - mainRoundOffset;
        return mainDrawSize >> (mainRoundIndex + 1);
    });

    if (mode === "bye") {
        const firstMainRound = mainRoundOffset;
        counts[firstMainRound] = Math.max(1, counts[firstMainRound] - byeCount);
    }
    if (hasBronzeMatch && participantCount >= 4) {
        counts[counts.length - 1] += 1;
    }
    return counts;
}

function defaultRoundConfig(name: string): MatchRoundConfig {
    return {
        name,
        courts: name === "Final" ? 1 : 2,
        game_rounds: 3,
        match_count: 0,
        round_time: 60,
        rest_time: 60,
        schedule: [],
    };
}

export function buildCategoryRounds(
    targetRoundNames: string[],
    sourceRoundNames: string[],
    sourceRounds: Record<number, MatchRoundConfig> | MatchRoundConfig[],
    cachedByName: Record<string, MatchRoundConfig> = {},
    playableCounts: number[] = [],
    tournamentCourts = MAX_PARALLEL_COURTS,
): {
    rounds: Record<number, MatchRoundConfig>;
    cache: Record<string, MatchRoundConfig>;
} {
    const cache = { ...cachedByName };
    sourceRoundNames.forEach((roundName, roundIndex) => {
        const round = sourceRounds?.[roundIndex];
        if (round) {
            cache[roundName] = { ...round, name: roundName };
        }
    });

    const rounds: Record<number, MatchRoundConfig> = {};
    targetRoundNames.forEach((roundName, roundIndex) => {
        const source = cache[roundName] || defaultRoundConfig(roundName);
        const maxCourts = maxParallelCourts(
            playableCounts[roundIndex] ?? MAX_PARALLEL_COURTS,
            tournamentCourts,
        );
        const courts = Math.min(Math.max(Number(source.courts) || 1, 1), maxCourts);
        rounds[roundIndex] = {
            ...source,
            name: roundName,
            courts,
            match_count: 0,
            schedule: [],
        };
    });
    return { rounds, cache };
}
